import {_storage} from "./_storage";
import {_event} from "./_events";
import {apiFetch} from "./apiFetch";
import {DEBUG} from "./_debug";

/**
 * Stan zalogowania z perspektywy klienta.
 *
 * Flaga `login` w localStorage jest tylko podpowiedzia UI - moze zniknac (czyszczenie danych strony,
 * zdarzenia typu clear-cache), mimo ze sesja serwerowa jest wazna. Zrodlem prawdy jest serwer (GET /session).
 *
 * Zasady (zmiana ma byc "nigdy gorzej niz dotad"):
 *  - flaga ustawiona -> true, bez sieci (offline dziala jak dotad),
 *  - flagi brak -> jedno zdeduplikowane zapytanie do serwera; potwierdzona sesja przywraca WYLACZNIE flage `login`
 *    (tak jak po udanym /login), nie dotyka `auth`/`token` (SMS i regulamin bez zmian),
 *  - kazdy inny wynik (przekierowanie, 403, 5xx, brak sieci) -> false, bez zadnych skutkow ubocznych
 *    (nie wylogowuje, nie czysci storage),
 *  - po wylogowaniu flaga nie jest odtwarzana az do kolejnego logowania.
 */

const NEGATIVE_TTL = 30000;
const PROBE_TIMEOUT = 8000;

let pending = null;
let lastNegativeAt = 0;
let loggingOut = false;

const hasFlag = async () => !!parseInt(await _storage.getItem('login'));

const probe = async () => {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = controller ? setTimeout(() => controller.abort(), PROBE_TIMEOUT) : null;
    try {
        const response = await apiFetch('/session', {
            credentials: 'same-origin',
            cache: 'no-store',
            // brak sesji = 302 na /login; nie podazamy za przekierowaniem (nie pobieramy strony logowania)
            redirect: 'manual',
            headers: {Accept: 'application/json'},
            ...(controller ? {signal: controller.signal} : {})
        });
        if (!response.ok || response.redirected) {
            return false;
        }
        const data = await response.json();
        return !!data && data.authenticated === true;
    } catch (e) {
        DEBUG && console.warn('[SESSION][PROBE][ERROR]', e);
        return false;
    } finally {
        timer && clearTimeout(timer);
    }
};

const isAuthenticated = async () => {
    if (await hasFlag()) {
        // flaga jest, wiec to nie jest okno po wylogowaniu
        loggingOut = false;
        return true;
    }
    if (loggingOut || (Date.now() - lastNegativeAt) < NEGATIVE_TTL) {
        return false;
    }
    if (!pending) {
        pending = probe()
            .then(async authenticated => {
                if (!authenticated) {
                    lastNegativeAt = Date.now();
                    return false;
                }
                if (loggingOut) {
                    // wylogowanie ruszylo w trakcie zapytania - nie odtwarzamy flagi
                    return false;
                }
                DEBUG && console.warn('[SESSION]', 'login flag missing, session valid - flag restored');
                await _storage.setItem('login', 1);
                return true;
            })
            .finally(() => {
                pending = null;
            });
    }
    return pending;
};

/**
 * Do wywolania synchronicznie tuz PRZED czyszczeniem storage przy wylogowaniu.
 */
const beginLogout = () => {
    loggingOut = true;
};

_event.addListener(_event.ON_LOGIN, () => {
    loggingOut = false;
    lastNegativeAt = 0;
});

export const Session = {
    isAuthenticated,
    beginLogout
};
